import React, { useState } from "react";
import { Box, IconButton, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * ReusableAccordion (Truly Reusable)
 * @param {React.ReactNode|function} summary - Content for the summary/header area (can be a node or a function that receives { expanded })
 * @param {React.ReactNode|function} details - Content for the details area (can be a node or a function that receives { expanded })
 * @param {boolean} expanded - Whether the accordion is expanded (controlled)
 * @param {function} onChange - Callback for expand/collapse
 * @param {object} summarySx - sx prop for the summary area (optional)
 * @param {object} detailsSx - sx prop for the details area (optional)
 * @param {object} rest - Other props
 */

const ReusableAccordion = ({
  summary,
  details,
  expanded: controlledExpanded,
  onChange,
  summarySx = {},
  detailsSx = {},
  ...rest
}) => {
  // If controlledExpanded is undefined, manage local state
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded =
    controlledExpanded !== undefined ? controlledExpanded : localExpanded;

  const handleToggle = () => {
    if (onChange) {
      onChange();
    } else {
      setLocalExpanded((prev) => !prev);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        mb: 2,
        ...rest.sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#f8fafc",
          minHeight: 0,
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          cursor: "pointer",
          borderRadius: 2,
          ...summarySx,
        }}
        onClick={handleToggle}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {typeof summary === "function" ? summary({ expanded }) : summary}
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          sx={{
            ml: 2,
            color: "primary.main",
            backgroundColor: expanded ? "#e1f1ff" : "#f0f7ff",
            "&:hover": { backgroundColor: "#e1f1ff" },
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      {/* Details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ background: "#fafbfc", p: 2, ...detailsSx }}>
          {typeof details === "function" ? details({ expanded }) : details}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ReusableAccordion;
